import { useQuery } from '@tanstack/react-query';

import { getExchangeRates } from '../api/currencies.api';
import { GetExchangeRatesRequest } from '../types/dto/request/get-exchange-rates-request';
import { ExchangeRates } from '../types/exchange-rates';

interface UseExchangeRatesParams {
  fromCodes: string[];
  toCode: string;
  revisionDate?: string;
  enabled?: boolean;
}

/**
 * Hook to fetch exchange rates for given currencies.
 *
 * `rates[fromCode]` is how many units of `fromCode` equal 1 unit of `toCode` —
 * divide a `fromCode` amount by it to convert to `toCode` (matches the backend's
 * own `CurrencyConverter`, which does `amount / rate`).
 *
 * @param params - Parameters including fromCodes, toCode, and optional revisionDate
 * @returns Query result with exchange rates
 *
 * @example
 * ```typescript
 * const { data: exchangeRates, isLoading } = useExchangeRates({
 *   fromCodes: ['USD', 'EUR'],
 *   toCode: 'GBP',
 * });
 *
 * if (exchangeRates) {
 *   const usdAmountInGbp = usdAmount / exchangeRates.rates['USD'];
 *   const eurAmountInGbp = eurAmount / exchangeRates.rates['EUR'];
 * }
 * ```
 */
export const useExchangeRates = ({
  fromCodes,
  toCode,
  revisionDate,
  enabled = true,
}: UseExchangeRatesParams) => {
  const request: GetExchangeRatesRequest = {
    fromCodes,
    toCode,
    revisionDate,
  };

  return useQuery<ExchangeRates, Error>({
    queryKey: ['exchange-rates', fromCodes, toCode, revisionDate],
    queryFn: () => getExchangeRates(request),
    enabled: enabled && fromCodes.length > 0 && Boolean(toCode),
    staleTime: 5 * 60 * 1000, // 5 minutes - exchange rates don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes cache
  });
};
